import { DependencyLink } from './types';

/**
 * Groups `links` into connected components of the undirected graph (direction
 * is ignored for membership). Clusters are returned by unique-node count,
 * largest first; `Array.prototype.sort` is stable, so ties fall back to
 * first-link discovery order.
 *
 * Vanilla union-find with path compression; near-linear in the number of links.
 */
export const splitIntoClusters = (links: DependencyLink[]): DependencyLink[][] => {
  const parent = new Map<string, string>();
  const find = (id: string): string => {
    // Walk to the root, recording the path as we go.
    const path: string[] = [];
    let current = id;
    while (true) {
      const next = parent.get(current);
      if (next === undefined || next === current) break;

      path.push(current);
      current = next;
    }
    // Path compression: point everything on the walked chain directly at the root.
    path.forEach((node) => parent.set(node, current));
    return current;
  };

  const union = (a: string, b: string): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  links.forEach(({ from, to }) => {
    if (!parent.has(from)) parent.set(from, from);
    if (!parent.has(to)) parent.set(to, to);

    union(from, to);
  });

  // Insertion-ordered `Map` preserves first-link discovery order per component;
  // we keep that order as the stable tiebreaker after sorting by node count.
  const groups = new Map<string, DependencyLink[]>();
  links.forEach((link) => {
    const root = find(link.from);
    const existing = groups.get(root);
    if (existing) {
      existing.push(link);
    } else {
      groups.set(root, [link]);
    }
  });

  const sized = Array.from(groups.values()).map((cluster) => {
    const nodes = new Set<string>();
    cluster.forEach(({ from, to }) => {
      nodes.add(from);
      nodes.add(to);
    });

    return { cluster, size: nodes.size };
  });

  sized.sort((a, b) => b.size - a.size);

  return sized.map(({ cluster }) => cluster);
};
